<template>
  <dd-modal
    v-if="details"
    v-model:visible="visibleProxy"
    :title="title"
    :self-close="false"
    :before-close="handleBeforeClose"
    desktop-sizing="small"
    append-to-body
    class="seat-limit-modal"
  >
    <p class="seat-limit-modal__body" v-html="bodyHtml" />
    <template #footer>
      <span>
        <dd-button :is-disabled="isSubmitting" type="text" @click="closeModal">{{ cancelLabel }}</dd-button>
        <dd-button
          v-if="canSelfServeUpgrade"
          :is-loading="isSubmitting"
          color="blue"
          @click="performUpgrade"
        >{{ ctaLabel }}</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

/* API */
import BillingAPI from '@/api/modules/billing.api'

/* Store */

/* Types */
import { IPlanLimitReachedDetails } from '@/api/types/response.types'
import { useProfileStore } from '@/modules/profile/profile.store'

/**
 * Modal that replaces the dead-end "you've reached your plan limit" toast for seat-related
 * PlanLimitReached errors. Branches on (isTrial, requesterIsOwner, isStartupPlan) to render the
 * right copy and CTA, then drives the upgrade through the BillingAPI in a single click.
 *
 * Trigger: parent catches a 403 with `error.data.details.limitType === 'seats'`, stashes the
 * details, and shows this modal.
 *
 * Auto-retry: on successful upgrade, emits `upgraded` so the parent can retry the original action
 * (e.g. invite the people the user was trying to add when they hit the limit).
 *
 * Trial-mode policy is enforced server-side; the modal just renders accordingly.
 */
export default defineComponent({
  name: 'SeatLimitModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    details: {
      type: Object as PropType<IPlanLimitReachedDetails | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'upgraded'],

  data() {
    return {
      isSubmitting: false,
    }
  },

  computed: {
    // Computed proxy so .sync from dd-modal flows back to the parent. Binding
    // :visible.sync directly to the `visible` prop would try to mutate the prop on close
    // (warning + silent no-op), so Esc / dd-modal's internal close paths wouldn't notify
    // the parent and the modal state would drift out of sync. Pattern matches
    // harvest-modal.vue.
    visibleProxy: {
      get(): boolean {
        return this.visible
      },
      set(newValue: boolean): void {
        this.$emit('update:visible', newValue)
      },
    },
    isTrial(): boolean {
      return this.details ? this.details.isTrial : false
    },
    isOwner(): boolean {
      return this.details ? this.details.requesterIsOwner : false
    },
    isStartup(): boolean {
      return this.details ? this.details.isStartupPlan : false
    },
    seatsInUse(): number {
      return this.details && this.details.seatsInUse !== null
        ? this.details.seatsInUse
        : 0
    },
    currentSeats(): number {
      return this.details && this.details.currentSeats !== null
        ? this.details.currentSeats
        : 0
    },
    targetSeats(): number {
      // Total seats (paid + free) — used for display copy ("we'll bump you to N seats").
      // Prefer the server's requestedSeats; fall back to "one more than current usage" for
      // resilience when the field isn't present.
      if (
        this.details &&
        this.details.requestedSeats !== null &&
        this.details.requestedSeats !== undefined
      ) {
        return this.details.requestedSeats
      }
      return this.seatsInUse + 1
    },
    targetPaidSeats(): number {
      // Paid seats — what the Billing endpoints actually consume. Falls back to targetSeats
      // so the call is still valid on older payloads that don't include requestedPaidSeats
      // (FreeSeats is 0 for ~all accounts today, so targetSeats and targetPaidSeats coincide).
      if (
        this.details &&
        this.details.requestedPaidSeats !== null &&
        this.details.requestedPaidSeats !== undefined
      ) {
        return this.details.requestedPaidSeats
      }
      return this.targetSeats
    },
    /**
     * Trial: anyone on the account can upgrade (no money moves until trial ends).
     * Post-trial: only the account owner can self-serve.
     */
    canSelfServeUpgrade(): boolean {
      return this.isTrial || this.isOwner
    },
    title(): string {
      if (!this.canSelfServeUpgrade) {
        return 'Seat limit reached'
      }
      if (this.isStartup) {
        return this.isTrial ? 'Time to upgrade' : 'Upgrade to add more people'
      }
      return 'Add more seats'
    },
    bodyHtml(): string {
      if (!this.canSelfServeUpgrade) {
        return (
          `Your account doesn't have room for more people right now (${
            this.seatsInUse
          } of ${this.currentSeats} seats used).` +
          ' Ask your account owner to upgrade so you can add more people.'
        )
      }
      if (this.isStartup) {
        if (this.isTrial) {
          return (
            `Your Starter plan allows ${this.currentSeats} seats. ` +
            `We'll upgrade you to <strong>Professional</strong> with <strong>${
              this.targetSeats
            } seats</strong> ` +
            'so you can add the rest of your team. <strong>No charge during your trial.</strong>'
          )
        }
        return (
          `Your Starter plan allows ${this.currentSeats} seats. ` +
          `Upgrade to <strong>Professional</strong> with <strong>${
            this.targetSeats
          } seats</strong> to keep going. ` +
          'You\'ll be charged a prorated amount for the new plan.'
        )
      }
      // Professional, self-serve.
      const additional = this.targetSeats - this.currentSeats
      const seatLabel = additional === 1 ? 'seat' : 'seats'
      if (this.isTrial) {
        return (
          `This puts you over your ${this.currentSeats}-seat limit. ` +
          `We'll bump your account to <strong>${
            this.targetSeats
          } seats</strong>. ` +
          '<strong>No charge during your trial.</strong>'
        )
      }
      return (
        `This puts you over your ${this.currentSeats}-seat limit. ` +
        `Add ${additional} more ${seatLabel} to keep going. ` +
        `You'll be charged a prorated amount for the new ${seatLabel}.`
      )
    },
    ctaLabel(): string {
      if (this.isStartup) {
        return 'Upgrade to Professional'
      }
      return 'Add seats'
    },
    cancelLabel(): string {
      return this.canSelfServeUpgrade ? 'Not now' : 'Got it'
    },
  },

  methods: {
    closeModal() {
      this.$emit('update:visible', false)
    },
    /**
     * Intercepts dd-modal's self-close paths (X icon, Esc). Blocks dismissal while an
     * upgrade request is in flight so the user can't walk away mid-call and then be
     * surprised by a success toast + auto-retry firing on a dismissed modal.
     */
    handleBeforeClose() {
      if (this.isSubmitting) {
        return
      }
      this.closeModal()
    },
    async performUpgrade() {
      if (!this.details) {
        return
      }

      this.isSubmitting = true
      try {
        if (this.isStartup) {
          await BillingAPI.post_UpgradeToProfessional({
            isSwitchingPeriod: false,
            targetSeats: this.targetPaidSeats,
          })
        } else {
          await BillingAPI.post_UpdateSeats({ seats: this.targetPaidSeats })
        }

        // Refresh profile so featureEnablement (plan, seats, isTrial) reflects the change in
        // any other components reading from $permissions / the store. PROFILE_FETCH replaces
        // profile.data wholesale — it does NOT carry profileMetrics — so we have to refetch
        // metrics too, otherwise $permissions.isAppSumoAccount throws on every subsequent read.
        await Promise.all([
          useProfileStore().profileFetch(),
          useProfileStore().profileMetricsFetch(),
        ])

        this.$messaging.showSuccess(
          this.isStartup ? 'You\'re on Professional now.' : 'Seats added.',
        )

        this.$emit('upgraded')
        this.closeModal()
      } catch (error) {
        this.$error.emit(error, true)
      }
      this.isSubmitting = false
    },
  },
})
</script>

<style lang="scss">
.seat-limit-modal {
  &__body {
    @include lettering--p-md;
    margin: 0;
  }
}
</style>
